import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import { Icons, Modal, PageTitle, PercentsSpinner, Table, UnavailableFeatureMessage } from '@components/common';

import useConsistencyCheck from '@hooks/consistencyCheck/useConsistencyCheck';
import useMutateConsistencyCheckBranchesExport from '@hooks/consistencyCheck/useMutateConsistencyCheckBranchesExport';
import { FULL_PERCENT } from '@hooks/constants';
import useQueryGoldenSettingsInfo from '@hooks/goldenSettings/useQueryGoldenSettingsInfo';
import { useVendorDrivenFeatureAvailability } from '@hooks/utils/useVendorDrivenFeatureAvailability';

import { configurationManagement } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { dateFormattingUTCDayMonthYear } from '@utils/date';
import { getDateWithoutOffset } from '@utils/kpi-utils/date';

import { ICMBranch } from '@interfaces/cmModule/cmModule';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort } from '@interfaces/components/table';

import { defaultMeta, EVendorsValues } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';
import { EParametersFormValues } from './enums';

import {
	consistencyCheckBranchesWidth,
	consistencyCheckPageTitle,
	consistencyCheckSearchTooltip,
	emptyTableText,
	goldenSettingsFormTitle,
	goldenSettingsFormWidth,
} from './constants';

import ConsistencyCheckDetails from './ConsistencyCheckDetails';
import ConsistencyCheckOptions from './ConsistencyCheckOptions';
import GoldenSettingsForm from './GoldenSettingsForm';
import { getColumns } from './manageMoBranch';

import styles from './ConsistencyCheck.module.scss';

const ConsistencyCheck = () => {
	const { selectedVendor } = useAppSelector(configurationManagement);

	const [selectedTableBranch, setSelectedTableBranch] = useState<ICMBranch | null>(null);
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const [isOpenConsistencyOptions, setIsOpenConsistencyOptions] = useState(true);
	const [isOpenGoldenSettingsForm, setIsOpenGoldenSettingsForm] = useState(false);
	const [isOpenParametersTable, setIsOpenParametersTable] = useState<boolean>(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	const {
		formState: { errors },
		handleSubmit,
		setValue,
		watch,
		control,
		register,
		resetField,
	} = useForm<FieldValues>({
		mode: EValidationMode.onSubmit,
		defaultValues: {
			[EParametersFormValues.vendor]: null,
			[EParametersFormValues.version]: null,
			[EParametersFormValues.configType]: null,
			[EParametersFormValues.technology]: null,
			[EParametersFormValues.nodeType]: null,
			[EParametersFormValues.configurationDate]: undefined,
			[EParametersFormValues.nodes]: [],
		},
	});

	useEffect(() => {
		if (selectedVendor) {
			setValue(EParametersFormValues.vendor, selectedVendor);
		}
	}, [selectedVendor, setValue]);

	const vendor = watch(EParametersFormValues.vendor)?.value;
	const technology = watch(EParametersFormValues.technology)?.value;
	const configType = watch(EParametersFormValues.configType)?.value;
	const version = watch(EParametersFormValues.version)?.value;
	const nodes = watch(EParametersFormValues.nodes);
	const configurationDate = watch(EParametersFormValues.configurationDate);
	const nodeType = watch(EParametersFormValues.nodeType)?.value;

	const nodeValues = useMemo(() => {
		return nodes.map(({ value }: { value: string }) => value);
	}, [nodes]);

	const isMavenirVendor = vendor === EVendorsValues.mavenir;

	const toggleIsOpenParametersTable = useCallback(() => setIsOpenParametersTable((prev) => !prev), []);
	const closeIsOpenParametersTable = useCallback(() => setIsOpenParametersTable(false), []);

	const closeGoldenSettingForm = useCallback(() => setIsOpenGoldenSettingsForm(false), []);
	const toggleGoldenSettingsForm = useCallback(() => setIsOpenGoldenSettingsForm((prev) => !prev), []);

	const toggleConsistencyOptions = useCallback(() => {
		setIsOpenConsistencyOptions(!isOpenConsistencyOptions);
	}, [isOpenConsistencyOptions]);

	const {
		generateProgressId,
		progressData,
		branch,
		countOfRecords,
		dumpId,
		datesOptions,
		dates,
		nodesTypesOptions,
		technologiesOptions,
		nodeOptions,
		configTypeOptions,
		versionOptions,
		isLoadingOptions,
		isLoadingBranchData,
	} = useConsistencyCheck({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		vendor,
		technology,
		nodeType,
		searchValue,
		configurationDate,
		isMavenirVendor,
		nodes: nodeValues,
		version,
		type: configType,
	});

	const isShowPercentSpinner = !!progressData?.percentsProgress && (progressData?.percentsProgress < FULL_PERCENT);

	const { goldenSettingsInfo } = useQueryGoldenSettingsInfo({
		vendor,
		technology,
		nodeType,
		nodes: nodeValues,
		version,
		type: configType,
	});

	const { handleConsistencyCheckBranchesExport } = useMutateConsistencyCheckBranchesExport();

	const goldenSettingsDate = useMemo(() => {
		return goldenSettingsInfo?.date
			? dateFormattingUTCDayMonthYear(goldenSettingsInfo.date)
			: undefined;
	}, [goldenSettingsInfo]);

	const isDisabledSubmit = isMavenirVendor
		? !vendor || !nodes || !version || !configType || !configurationDate || !goldenSettingsInfo
		: !vendor || !technology || !nodeType || !configurationDate || !goldenSettingsInfo;

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: consistencyCheckSearchTooltip,
	}], [searchValue, searchHandler]);

	useEffect(() => {
		setValue(EParametersFormValues.nodes, []);
		resetField(EParametersFormValues.technology);
		resetField(EParametersFormValues.nodeType);
		resetField(EParametersFormValues.configurationDate);
	}, [vendor, resetField, setValue]);

	useEffect(() => {
		resetField(EParametersFormValues.nodeType);
		resetField(EParametersFormValues.configurationDate);
	}, [technology, resetField]);

	useEffect(() => {
		setValue(EParametersFormValues.configurationDate, undefined);
	}, [nodeType, setValue]);

	useEffect(() => {
		resetField(EParametersFormValues.version);
	}, [nodes, resetField]);

	useEffect(() => {
		resetField(EParametersFormValues.configType);
	}, [version, resetField]);

	useEffect(() => {
		setValue(EParametersFormValues.configurationDate, undefined);
	}, [configType, setValue]);

	const openTableBranch = useCallback((data: ICMBranch) => {
		setSelectedTableBranch(data);
		toggleIsOpenParametersTable();
	}, [toggleIsOpenParametersTable, setSelectedTableBranch]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const columns = useMemo(() => getColumns(openTableBranch), [openTableBranch]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [countOfRecords, page, pageSize, pageHandler, pageSizeHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const onSubmit = useCallback(() => {
		generateProgressId();
		setPage(defaultMeta.page);
		setPageSize(defaultMeta.pageSize);
		setSearchValue('');
		setIsDirty(true);
		setIsOpenConsistencyOptions(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [generateProgressId, configurationDate]);

	const handleExport = useCallback(() => {
		if (!dumpId) return;

		return handleConsistencyCheckBranchesExport({ dumpId });
	}, [handleConsistencyCheckBranchesExport, dumpId]);

	const datesOptionsFormatted = useMemo(() => {
		return isMavenirVendor
			? datesOptions
			: dates.map((date) => getDateWithoutOffset(date));
	}, [dates, datesOptions, isMavenirVendor]);

	const isFeatureAvailable = useVendorDrivenFeatureAvailability();

	return isFeatureAvailable ? (
		<div className={styles.consistencyCheckContainer}>
			<div className={styles.pageTitleWrapper}>
				<PageTitle
					icon={Icons.consistencyActive()}
					title={consistencyCheckPageTitle}
				/>
			</div>
			<div className={styles.consistencyCheckContent}>
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyFormModal}
					isOpen={isOpenGoldenSettingsForm}
					onClose={closeGoldenSettingForm}
					title={goldenSettingsFormTitle}
					width={goldenSettingsFormWidth}
				>
					<GoldenSettingsForm
						closeGoldenSettingForm={closeGoldenSettingForm}
						isMavenirVendor={isMavenirVendor}
						nodeOptions={nodeOptions as TSelectOption<string>[]}
						nodeType={nodeType}
						nodes={nodeValues}
						onClose={closeGoldenSettingForm}
						setIsDirtyFormModal={setIsDirtyFormModal}
						technology={technology}
						type={configType}
						vendor={vendor}
						version={version}
					/>
				</Modal>
				<ConsistencyCheckOptions
					configTypeOptions={configTypeOptions}
					control={control}
					datesOptions={datesOptionsFormatted}
					errors={errors}
					goldenSettingsDate={goldenSettingsDate}
					handleSubmit={handleSubmit(onSubmit)}
					importFileName={goldenSettingsInfo?.importFileName}
					isDisabledSubmit={isDisabledSubmit || isLoadingBranchData}
					isLoading={isLoadingOptions}
					isMavenirVendor={isMavenirVendor}
					isOpenConsistencyOptions={isOpenConsistencyOptions}
					nodeOptions={nodeOptions}
					nodeTypesOptions={nodesTypesOptions}
					register={register}
					setValue={setValue}
					showGoldenSettings={toggleGoldenSettingsForm}
					technologiesOptions={technologiesOptions}
					toggleConsistencyOptions={toggleConsistencyOptions}
					versionOptions={versionOptions}
					watch={watch}
				/>
				<div className={styles.consistencyCheckTableWrapper}>
					{selectedTableBranch && (
						<Modal
							isOpen={!!(isOpenParametersTable && selectedTableBranch)}
							onClose={closeIsOpenParametersTable}
							title={selectedTableBranch?.distName}
							width={consistencyCheckBranchesWidth}
						>
							<ConsistencyCheckDetails
								isMavenirVendor={isMavenirVendor}
								selectedTableBranch={selectedTableBranch}
							/>
						</Modal>
					)}
					{!branch?.length && !isDirty
						? <div className={styles.emptyMessage}>{emptyTableText}</div>
						: <Table
							columns={columns}
							data={branch}
							exportTooltipText='Export Summary to Excel'
							handleCustomExport={handleExport}
							isExport
							isLoading={isLoadingBranchData}
							isWithoutActions
							isWithoutCheckbox
							isWithoutDeleteAction
							isWithoutFilter
							loadingComponent={isShowPercentSpinner
								? <PercentsSpinner
									percentsProgress={progressData?.percentsProgress}
									savedBranches={progressData?.savedBranches}
									totalBranchesToSave={progressData?.totalBranchesToSave}
								/>
								: undefined}
							pagination={pagination}
							search={search}
							searchThreshold={0}
							sort={sort}
						/>
					}
				</div>
			</div>
		</div>
	) : <UnavailableFeatureMessage vendorName={selectedVendor?.value} />;
};

export default ConsistencyCheck;
